import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Settings, Edit, DollarSign, Building2, ToggleLeft, Users, FileSearch } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHelpers';

const API = process.env.REACT_APP_BACKEND_URL;

// FormatCard component - extracted to avoid nested component definition
const FormatCard = ({ format, creditValue, onEditPricing, onToggleStatus }) => (
  <div
    className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors ${
      !format.is_active ? 'opacity-60 bg-gray-100' : ''
    }`}
  >
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-gray-800">{format.name}</span>
        <Badge variant={format.is_active ? "default" : "secondary"}>
          {format.is_active ? 'Active' : 'Inactive'}
        </Badge>
        <Badge variant="outline" className={format.category === 'APF' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}>
          {format.category}
        </Badge>
      </div>
      <div className="text-sm text-gray-600 mt-1">{format.description}</div>
      <div className="text-xs text-gray-400 mt-1">Bank Code: {format.bank_code}</div>
    </div>
    <div className="flex items-center gap-4">
      <div className="text-right">
        <div className="text-2xl font-bold text-gray-800">
          {format.wallet_cost} {format.wallet_cost === 1 ? 'credit' : 'credits'}
        </div>
        <div className="text-sm text-gray-500">
          ₹{(format.cost_inr || format.wallet_cost * creditValue).toLocaleString('en-IN')}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEditPricing(format)}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Switch
          checked={format.is_active}
          onCheckedChange={() => onToggleStatus(format)}
        />
      </div>
    </div>
  </div>
);

const OpinionFormatPricing = () => {
  const [formats, setFormats] = useState([]);
  const [creditValue, setCreditValue] = useState(500);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFormat, setEditingFormat] = useState(null);
  const [newCost, setNewCost] = useState(0);
  const [reason, setReason] = useState('');
  
  // Tenant format settings
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [tenantFormatSelections, setTenantFormatSelections] = useState({});

  useEffect(() => {
    fetchFormats();
    fetchTenants();
  }, []);

  const fetchFormats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/opinion-formats/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFormats(response.data.formats || []);
      setCreditValue(response.data.credit_value_inr || response.data.price_per_credit_inr || 500);
    } catch (error) {
      console.error('Error fetching formats:', error);
      toast.error('Failed to load opinion formats');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/tenants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTenants(response.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const handleEditPricing = (format) => {
    setEditingFormat(format);
    setNewCost(format.wallet_cost);
    setReason('');
    setShowEditModal(true);
  };

  const handleUpdatePricing = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/admin/opinion-formats/${editingFormat.id}/pricing`,
        {
          wallet_cost: parseFloat(newCost),
          reason: reason
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(`Pricing updated for ${editingFormat.name}`);
      setShowEditModal(false);
      fetchFormats();
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error(getErrorMessage(error, 'Failed to update pricing'));
    }
  };

  const handleToggleStatus = async (format) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/admin/opinion-formats/${format.id}/status`,
        { is_active: !format.is_active },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(`${format.name} ${format.is_active ? 'deactivated' : 'activated'}`);
      fetchFormats();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error(getErrorMessage(error, 'Failed to update format status'));
    }
  };

  const handleManageTenantFormats = async (tenant) => {
    setSelectedTenant(tenant);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/admin/opinion-formats/tenant/${tenant.id}/settings`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setTenantSettings(response.data);
      
      // Initialize selections
      const selections = {};
      const enabledIds = response.data.enabled_format_ids || [];
      const disabledIds = response.data.disabled_format_ids || [];
      
      formats.forEach(f => {
        if (enabledIds.length > 0) {
          selections[f.id] = enabledIds.includes(f.id);
        } else if (disabledIds.length > 0) {
          selections[f.id] = !disabledIds.includes(f.id);
        } else {
          selections[f.id] = f.is_active;
        }
      });
      setTenantFormatSelections(selections);
      setShowTenantModal(true);
    } catch (error) {
      console.error('Error fetching tenant settings:', error);
      toast.error(getErrorMessage(error, 'Failed to load tenant format settings'));
    }
  };

  const handleSaveTenantSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const enabledIds = Object.keys(tenantFormatSelections).filter(id => tenantFormatSelections[id]);
      
      await axios.put(
        `${API}/api/admin/opinion-formats/tenant/${selectedTenant.id}/settings`,
        { enabled_format_ids: enabledIds },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(`Format settings updated for ${selectedTenant.name}`);
      setShowTenantModal(false);
    } catch (error) {
      console.error('Error saving tenant settings:', error);
      toast.error(getErrorMessage(error, 'Failed to save tenant format settings'));
    }
  };

  const handleResetTenantSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/api/admin/opinion-formats/tenant/${selectedTenant.id}/settings`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(`Format settings reset for ${selectedTenant.name}`);
      setShowTenantModal(false);
    } catch (error) {
      console.error('Error resetting tenant settings:', error);
      toast.error(getErrorMessage(error, 'Failed to reset tenant format settings'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const retailFormats = formats.filter(f => f.category === 'RETAIL');
  const apfFormats = formats.filter(f => f.category === 'APF');
  const scrutinyFormats = formats.filter(f => f.category === 'SCRUTINY');

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Opinion Format Pricing</h1>
              <p className="text-gray-600">Manage pricing for all opinion formats</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">1 Credit =</div>
            <div className="text-2xl font-bold text-blue-600">₹{creditValue.toLocaleString('en-IN')}</div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Retail Formats</p>
                  <p className="text-3xl font-bold">{retailFormats.length}</p>
                </div>
                <Building2 className="w-10 h-10 opacity-50" />
              </div>
              <p className="text-xs text-blue-100 mt-2">
                {retailFormats.length > 0 
                  ? `${retailFormats[0]?.wallet_cost || 1} credit each (₹${((retailFormats[0]?.wallet_cost || 1) * creditValue).toLocaleString('en-IN')})`
                  : '1 credit each'}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">APF Formats</p>
                  <p className="text-3xl font-bold">{apfFormats.length}</p>
                </div>
                <DollarSign className="w-10 h-10 opacity-50" />
              </div>
              <p className="text-xs text-purple-100 mt-2">
                {apfFormats.length > 0 
                  ? `${apfFormats[0]?.wallet_cost || 4} credits each (₹${((apfFormats[0]?.wallet_cost || 4) * creditValue).toLocaleString('en-IN')})`
                  : '4 credits each'}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Scrutiny Formats</p>
                  <p className="text-3xl font-bold">{scrutinyFormats.length}</p>
                </div>
                <FileSearch className="w-10 h-10 opacity-50" />
              </div>
              <p className="text-xs text-green-100 mt-2">
                {scrutinyFormats.length > 0 
                  ? `${scrutinyFormats[0]?.wallet_cost || 2} credits each (₹${((scrutinyFormats[0]?.wallet_cost || 2) * creditValue).toLocaleString('en-IN')})`
                  : '2 credits each'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Format Lists */}
        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all">All Formats ({formats.length})</TabsTrigger>
            <TabsTrigger value="retail">Retail ({retailFormats.length})</TabsTrigger>
            <TabsTrigger value="apf">APF ({apfFormats.length})</TabsTrigger>
            <TabsTrigger value="scrutiny">Scrutiny ({scrutinyFormats.length})</TabsTrigger>
            <TabsTrigger value="tenants">Tenant Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle>All Opinion Formats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {formats.map((format) => (
                  <FormatCard 
                    key={format.id} 
                    format={format} 
                    creditValue={creditValue}
                    onEditPricing={handleEditPricing}
                    onToggleStatus={handleToggleStatus}
                  />
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="retail">
            <Card>
              <CardHeader>
                <CardTitle className="text-blue-600">Retail Formats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {retailFormats.map((format) => (
                  <FormatCard 
                    key={format.id} 
                    format={format} 
                    creditValue={creditValue}
                    onEditPricing={handleEditPricing}
                    onToggleStatus={handleToggleStatus}
                  />
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="apf">
            <Card>
              <CardHeader>
                <CardTitle className="text-purple-600">APF Formats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {apfFormats.map((format) => (
                  <FormatCard 
                    key={format.id} 
                    format={format} 
                    creditValue={creditValue}
                    onEditPricing={handleEditPricing}
                    onToggleStatus={handleToggleStatus}
                  />
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="scrutiny">
            <Card>
              <CardHeader>
                <CardTitle>Scrutiny Formats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {scrutinyFormats.map((format) => (
                  <FormatCard 
                    key={format.id} 
                    format={format} 
                    creditValue={creditValue}
                    onEditPricing={handleEditPricing}
                    onToggleStatus={handleToggleStatus}
                  />
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tenants">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Tenant Format Access
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Configure which formats each tenant can access. By default, tenants have access to all active formats.
                </p>
                <div className="space-y-2">
                  {tenants.map((tenant) => (
                    <div
                      key={tenant.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div>
                        <div className="font-medium">{tenant.name}</div>
                        <div className="text-sm text-gray-500">{tenant.id}</div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleManageTenantFormats(tenant)}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Manage Formats
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Info Card */}
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="text-sm text-yellow-800">
              <strong>⚠️ Important:</strong> Changes to pricing take effect immediately for new operations.
              Existing operations are not affected. Format deactivation hides the format from users but does not affect existing opinions.
            </div>
          </CardContent>
        </Card>

        {/* Edit Pricing Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle>Update Pricing: {editingFormat?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <div className="text-sm text-gray-600 mb-2">Current Pricing</div>
                <div className="text-2xl font-bold">
                  {editingFormat?.wallet_cost} credits
                  <span className="text-lg text-gray-500 ml-2">
                    (₹{((editingFormat?.wallet_cost || 0) * creditValue).toLocaleString('en-IN')})
                  </span>
                </div>
              </div>
              <div>
                <Label>New Credits Per Operation *</Label>
                <Input
                  type="number"
                  value={newCost}
                  onChange={(e) => setNewCost(e.target.value)}
                  min="0.1"
                  step="0.1"
                />
                <div className="text-sm text-gray-500 mt-1">
                  New cost: ₹{(newCost * creditValue).toLocaleString('en-IN')}
                </div>
              </div>
              <div>
                <Label>Reason for Change *</Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Market adjustment, Cost optimization"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdatePricing}
                disabled={!reason || reason.length < 3 || newCost < 0.1}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Update Pricing
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tenant Format Settings Modal */}
        <Dialog open={showTenantModal} onOpenChange={setShowTenantModal}>
          <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Format Access: {selectedTenant?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Select which formats this tenant can access. Unchecked formats will be hidden from the tenant.
              </p>
              
              <div className="space-y-2">
                <div className="font-medium text-blue-600 mb-2">Retail Formats</div>
                {retailFormats.map((format) => (
                  <div key={format.id} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <span>{format.name}</span>
                      {!format.is_active && <Badge variant="secondary">Inactive</Badge>}
                    </div>
                    <Switch
                      checked={tenantFormatSelections[format.id] || false}
                      onCheckedChange={(checked) => 
                        setTenantFormatSelections(prev => ({ ...prev, [format.id]: checked }))
                      }
                    />
                  </div>
                ))}
              </div>

              <div className="space-y-2 mt-4">
                <div className="font-medium text-purple-600 mb-2">APF Formats</div>
                {apfFormats.map((format) => (
                  <div key={format.id} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex items-center gap-2">
                      <span>{format.name}</span>
                      {!format.is_active && <Badge variant="secondary">Inactive</Badge>}
                    </div>
                    <Switch
                      checked={tenantFormatSelections[format.id] || false}
                      onCheckedChange={(checked) => 
                        setTenantFormatSelections(prev => ({ ...prev, [format.id]: checked }))
                      }
                    />
                  </div>
                ))}
              </div>

              {scrutinyFormats.length > 0 && (
                <div className="space-y-2 mt-4">
                  <div className="font-medium text-green-600 mb-2">Scrutiny Formats</div>
                  {scrutinyFormats.map((format) => (
                    <div key={format.id} className="flex items-center justify-between p-2 border rounded">
                      <div className="flex items-center gap-2">
                        <span>{format.name}</span>
                        {!format.is_active && <Badge variant="secondary">Inactive</Badge>}
                      </div>
                      <Switch
                        checked={tenantFormatSelections[format.id] || false}
                        onCheckedChange={(checked) => 
                          setTenantFormatSelections(prev => ({ ...prev, [format.id]: checked }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter className="flex justify-between">
              <Button variant="outline" onClick={handleResetTenantSettings}>
                Reset to Default
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowTenantModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveTenantSettings} className="bg-blue-600 hover:bg-blue-700">
                  Save Settings
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default OpinionFormatPricing;
